import React, { useCallback, useEffect, useState } from 'react';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Grid from '@mui/material/Grid';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import FormGroup from '@mui/material/FormGroup';
import { MUIDataTableState, MUISortOptions } from 'mui-datatables';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import { DateTime } from 'luxon';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import ScheduleIcon from '@mui/icons-material/Schedule';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import EventBusyIcon from '@mui/icons-material/EventBusy';

import { EMPTY_FIELD_VALUE } from 'helpers/constants';
import { cnpjMask, unmaskDocument } from 'helpers/mask';
import { formatDate } from 'helpers/date';
import { hasReportSource } from 'helpers/utils';

import DataTable from 'components/Shared/Table';
import TopPage from 'components/Shared/TopPage';
import Loader from 'components/Shared/Loader';
import FormSelect from 'components/Shared/FormFields/FormSelect';
import FormSwitch from 'components/Shared/FormFields/FormSwitch';
import FormText from 'components/Shared/FormFields/FormText';
import FormAutoComplete from 'components/Shared/FormFields/FormAutoComplete';
import {
  getInfractions,
  infraction,
  downloadInfractions,
  getInfractionsQuickFilters,
  QuickFilterItem,
  downloadInfractionsFastFilter
} from 'services/antt';
import { getLicensePlateOwnersOptions } from 'services/licensePlateOwner';
import {
  getAnttAuthoritySituationOptions,
  getAnttInfractionStatusOptions,
  getAnttInfractionSubtypeOptions,
  getAnttInfractionTypeOptions,
  getAnttInvoiceStatusOptions,
  getAuthorities,
  getClientOptions,
  Option,
  Options
} from 'services/filters';
import { ROLE, User } from 'services/auth';
import { useSnackbar } from 'notistack';
import CartFastFilter from 'components/Shared/Table/fast-filters';
import { buildQuickFilterUrl } from 'helpers/formats';
import { columns, filterByOptions, filterByPeriodOptions } from './constants';
import type { FilterItem, ToggleItem } from './constants';
import {
  CustomAccordion,
  CustomAccordionSummary,
  CustomAccordionDetails,
  CustomLoadingButton,
  CustomCardContent,
  WrapperCartFastFilter,
  TitleCartFastFilter,
  FiltersContainer
} from './styles';

const QUICK_FILTER_ID = {
  RECENT_INFRACTIONS: 1,
  AVAILABLE_INVOICES: 2,
  INVOICES_DUE_SOON: 3,
  SERASA_INVOICES: 4,
  EXPIRED_INVOICES: 5
} as const;

const FAST_FILTER_ICONS: Record<number, React.ReactNode> = {
  [QUICK_FILTER_ID.RECENT_INFRACTIONS]: <WarningAmberIcon fontSize='inherit' />,
  [QUICK_FILTER_ID.AVAILABLE_INVOICES]: <ReceiptLongIcon fontSize='inherit' />,
  [QUICK_FILTER_ID.INVOICES_DUE_SOON]: <ScheduleIcon fontSize='inherit' />,
  [QUICK_FILTER_ID.SERASA_INVOICES]: <AccountBalanceIcon fontSize='inherit' />,
  [QUICK_FILTER_ID.EXPIRED_INVOICES]: <EventBusyIcon fontSize='inherit' />
};

const DEFAULT_FAST_FILTER_ICON = <FilterAltIcon fontSize='inherit' />;

const defaultSort: MUISortOptions = {
  name: 'datetime',
  direction: 'desc'
};

function TrafficTickets() {
  let timeCounter: number;
  const { enqueueSnackbar } = useSnackbar();
  const [data, setData] = useState<infraction[] | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isInitialLoading, setIsInitialLoading] = useState<boolean>(true);
  const [filterIsLoading, setFilterIsLoading] = useState<boolean>(false);
  const [exportIsLoading, setExportIsLoading] = useState<boolean>(false);
  const [hasFilterSelected, setHasFilterSelected] = useState<boolean>(false);
  const [filterList, setFilterList] = useState<FilterItem[]>([]);
  const [toggleList, setToggleList] = useState<ToggleItem[]>([]);
  const [urlFilter, setUrlFilter] = useState<string>('');

  const [companySelected, setCompanySelected] = useState('');
  const [typeSelected, setTypeSelected] = useState('');
  const [subtypeSelected, setSubtypeSelected] = useState('');
  const [statusSelected, setStatusSelected] = useState('');
  const [situationSelected, setSituationSelected] = useState('');
  const [authoritySelected, setAuthoritySelected] = useState('');
  const [paymentStatusSelected, setPaymentStatusSelected] = useState('');
  const [plateOwner, setPlateOwner] = useState<Option | null>(null);
  const [filterBy, setFilterBy] = useState('');
  const [filterByPeriod, setFilterByPeriod] = useState('');
  const [initialDate, setinitialDate] = useState('');
  const [finalDate, setfinalDate] = useState('');

  const [search, setSearch] = useState('');

  const [pagePagination, setPagePagination] = useState<number>(1);
  const [perPagePagination, setPerPagePagination] = useState<number>(10);
  const [countPagination, setCountPagination] = useState<number>(0);
  const [sortOptions, setSortOptions] = useState<MUISortOptions>(defaultSort);

  const [companyList, setCompanyList] = useState<Options>([]);
  const [typeList, setTypeList] = useState<Options>([]);
  const [subtypeList, setSubtypeList] = useState<Options>([]);
  const [statusList, setStatusList] = useState<Options>([]);
  const [situationList, setSituationList] = useState<Options>([]);
  const [auhtorityList, setAuhtorityList] = useState<Options>([]);
  const [paymentStatusList, setPaymentStatusList] = useState<Options>([]);
  const [plateOwnerList, setPlateOwnerList] = useState<Options>([]);

  const [quickFilters, setQuickFilters] = useState<QuickFilterItem[]>([]);
  const [selectedFastFilter, setSelectedFastFilter] = useState<QuickFilterItem | null>(null);

  const userSerialized = localStorage.getItem('user') || '';
  const storageUser: User = JSON.parse(userSerialized);
  const isLegalExternal: boolean | undefined = storageUser.roles[0].name === ROLE.LEGAL_EXTERNAL;

  const handleSuccess = (newData: infraction[], newCount: number) => {
    setIsLoading(false);
    setIsInitialLoading(false);
    setData(newData);
    setCountPagination(newCount);
    setFilterIsLoading(false);
  };

  const handleFetchError = useCallback(() => {
    setIsLoading(false);
    setIsInitialLoading(false);
    setFilterIsLoading(false);
    enqueueSnackbar('Erro ao carregar as multas. Verifique os filtros e tente novamente.', {
      variant: 'error'
    });
  }, [enqueueSnackbar]);

  const loadFiltersLists = () => {
    getClientOptions((options: Options) => setCompanyList(options));
    getAuthorities((options: Options) => setAuhtorityList(options));
    getAnttInfractionTypeOptions((options: Options) => setTypeList(options));
    getAnttInfractionSubtypeOptions((options: Options) => setSubtypeList(options));
    getAnttInfractionStatusOptions((options: Options) => setStatusList(options));
    getAnttAuthoritySituationOptions((options: Options) => setSituationList(options));
    getAnttInvoiceStatusOptions((options: Options) => setPaymentStatusList(options));
    getLicensePlateOwnersOptions((options: Options) => setPlateOwnerList(options));
  };

  const fetchData = useCallback(
    (
      perPage?: number,
      page?: number,
      search?: string,
      filters?: string,
      dateRef?: string,
      from?: string,
      to?: string,
      plateOwner?: Option | null
    ) => {
      setIsLoading(true);
      let defaultFilter = `&columns[0][name]=${defaultSort.name}&columns[0][order]=${defaultSort.direction}`;

      if (isLegalExternal) {
        defaultFilter += '&toggles[0]=forwardedToLegal';
      }

      getInfractions(
        handleSuccess,
        {
          perPage: perPage || 10,
          page: page || 1,
          search: search || '',
          filters: filters || defaultFilter,
          dateRef,
          from,
          to,
          plateOwner: plateOwner
            ? { cnpj: plateOwner.data.responsable_cnpj || '', name: plateOwner.data.responsable_name || '' }
            : null
        },
        handleFetchError
      );
    },
    [isLegalExternal, handleFetchError]
  );

  const clearAllFilters = () => {
    setCompanySelected('');
    setTypeSelected('');
    setSubtypeSelected('');
    setStatusSelected('');
    setSituationSelected('');
    setAuthoritySelected('');
    setPaymentStatusSelected('');
    setPlateOwner(null);
    setFilterBy('');
    setFilterByPeriod('');
    setFilterList([] as FilterItem[]);
    setHasFilterSelected(false);
    setinitialDate('');
    setfinalDate('');
    setToggleList([] as ToggleItem[]);
    setUrlFilter('');
    setSearch('');
    setSelectedFastFilter(null);
    setPerPagePagination(10);
    setPagePagination(1);
  };

  const onAppyFastFilter = (item: QuickFilterItem | null, page?: number, rowsPerPage?: number) => {
    if (!item) {
      clearAllFilters();
      fetchData();
      return;
    }

    const pageToApply = page ?? 1;
    const rowsPerPageToApply = rowsPerPage ?? perPagePagination;

    setSelectedFastFilter(item);
    setPagePagination(pageToApply);
    setPerPagePagination(rowsPerPageToApply);

    const filter = buildQuickFilterUrl(item.filter);

    fetchData(rowsPerPageToApply, pageToApply, unmaskDocument(search), filter, '', '', '', plateOwner);
  };

  const onChangePage = (page: number) => {
    if (selectedFastFilter) {
      onAppyFastFilter(selectedFastFilter, page, perPagePagination);
      return;
    }

    setPagePagination(page);

    fetchData(perPagePagination, page, unmaskDocument(search), urlFilter, filterBy, initialDate, finalDate, plateOwner);
  };

  const onChangeRowsPerPage = (rowsPerPage: number) => {
    if (selectedFastFilter) {
      onAppyFastFilter(selectedFastFilter, 1, rowsPerPage);
      return;
    }

    setPerPagePagination(rowsPerPage);

    fetchData(
      rowsPerPage,
      pagePagination,
      unmaskDocument(search),
      urlFilter,
      filterBy,
      initialDate,
      finalDate,
      plateOwner
    );
  };

  const onSearch = (search: string) => {
    window.clearTimeout(timeCounter);

    timeCounter = window.setTimeout(() => {
      const validLenght = search.length === 0 || search.length > 2;

      const triggSearch = () => {
        setSearch(search);
        fetchData(
          perPagePagination,
          pagePagination,
          unmaskDocument(search),
          urlFilter,
          filterBy,
          initialDate,
          finalDate,
          plateOwner
        );
      };

      if (validLenght) triggSearch();
    }, 2000);
  };

  const onChangeFilterBy = (filter: string) => {
    setFilterBy(filter);
    setHasFilterSelected(filterByPeriod !== '');
  };

  const onChangeFilterByPeriod = (period: string) => {
    setFilterByPeriod(period);
    setHasFilterSelected(filterBy !== '');

    const dateFormat = 'yyyy-MM-dd';
    const today = DateTime.now();
    switch (period) {
      case 'week':
      case 'month':
      case 'year':
        setinitialDate(today.startOf(period).toFormat(dateFormat));
        break;
      case '30':
      case '365':
        setinitialDate(today.minus({ days: Number(period) }).toFormat(dateFormat));
        break;

      default:
        setinitialDate(today.toFormat(dateFormat));
        break;
    }
    setfinalDate(today.toFormat(dateFormat));
  };

  const onChangeFilterPlateOwner = (owner: Option) => {
    setPlateOwner(owner);
    setHasFilterSelected(true);
  };

  const addFilter = (name: string, value: unknown): void => {
    const index = filterList.findIndex(filter => filter.name === name);

    if (index < 0) {
      filterList.push({
        name: name,
        filter: value
      });
    } else {
      filterList[index].filter = value;
    }

    setFilterList(filterList);
  };

  const removeFilter = (name: string): void => {
    const newFilterList = filterList.filter(filter => {
      return filter.name !== name;
    });

    setFilterList(newFilterList);
  };

  const createURLFilter = (sortOpts: MUISortOptions): string | null => {
    if (filterList.length === 0) {
      return `&columns[0][name]=${sortOpts.name}&columns[0][order]=${sortOpts.direction}`;
    }

    let sortUrlCreated = false;
    let urlFilter = '';
    filterList.forEach((filterItem: FilterItem, index: number) => {
      urlFilter += `&columns[${index}][name]=${filterItem.name}`;
      urlFilter += filterItem.filter ? `&columns[${index}][filter]=${filterItem.filter}` : '';

      if (filterItem.name === sortOpts.name) {
        urlFilter += `&columns[${index}][order]=${sortOpts.direction}`;
        sortUrlCreated = true;
      }
    });

    if (!sortUrlCreated) {
      const lastIndex = filterList.length;
      urlFilter += `&columns[${lastIndex}][name]=${sortOpts.name}&columns[${lastIndex}][order]=${sortOpts.direction}`;
    }

    return urlFilter;
  };

  const createURLToggle = (): string | null => {
    if (toggleList.length === 0) {
      return null;
    }

    let urlFilter = '';
    toggleList.forEach((toggleItem: ToggleItem, index: number) => {
      urlFilter += `&toggles[${index}]=${toggleItem.name}`;
    });

    return urlFilter;
  };

  const onFilter = (): void => {
    setFilterIsLoading(true);
    setPerPagePagination(10);
    setPagePagination(1);

    let filter = '';
    filter += createURLFilter(sortOptions) ?? '';
    filter += createURLToggle() ?? '';

    setUrlFilter(filter);
    setPagePagination(1);
    fetchData(
      perPagePagination,
      pagePagination,
      unmaskDocument(search),
      filter,
      filterBy,
      initialDate,
      finalDate,
      plateOwner
    );

    setSelectedFastFilter(null);
  };

  type setState = React.Dispatch<React.SetStateAction<string>>;

  const onClear = (_event: React.MouseEvent<HTMLButtonElement, MouseEvent>, setState: setState, name: string) => {
    setState('');
    removeFilter(name);
  };

  const onAutoCompleteSelectedFilter = (opt: Option, name: string, setState: setState) => {
    const value = opt?.value || '';
    setState(value);
    setHasFilterSelected(true);
    addFilter(name, value);
  };

  const onSortTable = (sortOpts: MUISortOptions) => {
    setSortOptions(sortOpts);

    let filter = '';
    filter += createURLFilter(sortOpts) ?? '';
    filter += createURLToggle() ?? '';

    setUrlFilter(filter);
    fetchData(
      perPagePagination,
      pagePagination,
      unmaskDocument(search),
      filter,
      filterBy,
      initialDate,
      finalDate,
      plateOwner
    );
  };

  const onToggle = (name: string, checked: boolean) => {
    if (checked) {
      toggleList.push({ name: name });

      setToggleList(toggleList);
      setHasFilterSelected(true);

      return;
    }

    const newToggleList: ToggleItem[] = toggleList.filter(toggle => {
      return toggle.name !== name;
    });

    setToggleList(newToggleList);
  };

  const onExport = () => {
    let filter = '';
    const hasFastFilterApplied = selectedFastFilter !== null;

    filter += createURLFilter(sortOptions) ?? '';
    filter += createURLToggle() ?? '';

    setExportIsLoading(true);

    if (hasFastFilterApplied && selectedFastFilter) {
      const fastFilter = buildQuickFilterUrl(selectedFastFilter.filter);

      downloadInfractionsFastFilter(() => setExportIsLoading(false), fastFilter);

      return;
    }

    downloadInfractions(() => setExportIsLoading(false), {
      perPage: perPagePagination || 10,
      page: pagePagination || 1,
      search: unmaskDocument(search) || '',
      filters: filter,
      dateRef: filterBy,
      from: initialDate,
      to: finalDate,
      plateOwner: plateOwner
        ? { cnpj: plateOwner.data.responsable_cnpj || '', name: plateOwner.data.responsable_name || '' }
        : null
    });
  };

  const loadQuickFilters = () => {
    getInfractionsQuickFilters(
      filters => setQuickFilters(filters),
      () => enqueueSnackbar('Erro ao carregar os filtros rápidos.', { variant: 'error' })
    );
  };

  useEffect(() => {
    loadFiltersLists();
    loadQuickFilters();

    if (isLegalExternal) {
      onToggle('forwardedToLegal', true);
    }

    return fetchData();
    // eslint-disable-next-line
  }, [fetchData]);

  const formattedData = data?.map((item: infraction) => {
    const { infraction_date, authority_name, subtype, status, cnpj } = item;
    const dateFormatted = infraction_date ? formatDate(infraction_date) : EMPTY_FIELD_VALUE;
    const cnpjFormatted = cnpjMask(cnpj);

    return {
      ...item,
      cnpj: cnpjFormatted,
      datetime: dateFormatted,
      authority: authority_name,
      status: status || '-',
      subtype: subtype || '-',
      isReportSource: hasReportSource(item.details)
    };
  });

  const filter = {
    searchText: search
  };

  const pagination = {
    page: pagePagination - 1,
    count: countPagination,
    rowsPerPage: perPagePagination,
    sortOrder: sortOptions,
    onTableChange: (action: string, tableState: MUIDataTableState) => {
      switch (action) {
        case 'changePage':
          return onChangePage(tableState.page + 1);
        case 'changeRowsPerPage':
          return onChangeRowsPerPage(tableState.rowsPerPage);
        case 'search':
          return onSearch(tableState.searchText || '');
        case 'sort':
          return onSortTable(tableState.sortOrder);
        default:
          return console.info(`action (${action}) not handled.`);
      }
    }
  };

  const optionsObj = {
    ...filter,
    ...pagination,
    jumpToPage: true
  };

  return (
    <>
      <TopPage title='Multas' />

      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Card style={CustomCardContent}>
            <CardContent>
              <FiltersContainer>
                {!isInitialLoading && quickFilters.length > 0 && (
                  <>
                    <TitleCartFastFilter>
                      <p>Filtros rápidos</p>
                      <hr />
                    </TitleCartFastFilter>
                    <WrapperCartFastFilter>
                      {quickFilters.map((item: QuickFilterItem) => (
                        <CartFastFilter
                          key={item.id}
                          value={item.count}
                          title={item.label}
                          icon={FAST_FILTER_ICONS[item.id] ?? DEFAULT_FAST_FILTER_ICON}
                          selected={item.id === selectedFastFilter?.id}
                          onSelect={() => onAppyFastFilter(item)}
                          onCancelClick={() => onAppyFastFilter(null)}
                        />
                      ))}
                    </WrapperCartFastFilter>
                  </>
                )}

                {!isInitialLoading && (
                  <CustomAccordion elevation={0}>
                    <CustomAccordionSummary
                      expandIcon={<ExpandMoreIcon />}
                      aria-controls='panel1a-content'
                      id='panel1a-header'
                    >
                      <FilterAltIcon />
                      <p>Filtros detalhados</p>
                    </CustomAccordionSummary>
                    <CustomAccordionDetails>
                      <Grid container spacing={3}>
                        <Grid item xs={3}>
                          <FormAutoComplete
                            id='cnpj'
                            label='CNPJ'
                            options={companyList}
                            value={companySelected}
                            onChange={(_, newValue) =>
                              onAutoCompleteSelectedFilter(newValue as Option, 'cnpj', setCompanySelected)
                            }
                            onClear={event => onClear(event, setCompanySelected, 'cnpj')}
                            disabled={filterIsLoading}
                          />
                        </Grid>

                        <Grid item xs={3}>
                          <FormAutoComplete
                            id='type'
                            label='Tipo'
                            value={typeSelected}
                            onChange={(_, newValue) =>
                              onAutoCompleteSelectedFilter(newValue as Option, 'type', setTypeSelected)
                            }
                            onClear={event => onClear(event, setTypeSelected, 'type')}
                            options={typeList}
                            disabled={filterIsLoading}
                          />
                        </Grid>

                        <Grid item xs={3}>
                          <FormAutoComplete
                            id='subtype'
                            label='Sub Tipo'
                            value={subtypeSelected}
                            onChange={(_, newValue) =>
                              onAutoCompleteSelectedFilter(newValue as Option, 'subtype', setSubtypeSelected)
                            }
                            options={subtypeList}
                            onClear={event => onClear(event, setSubtypeSelected, 'subtype')}
                            disabled={filterIsLoading}
                          />
                        </Grid>

                        <Grid item xs={3}>
                          <FormAutoComplete
                            id='status'
                            label='Status'
                            value={statusSelected}
                            onChange={(_, newValue) =>
                              onAutoCompleteSelectedFilter(newValue as Option, 'status', setStatusSelected)
                            }
                            options={statusList}
                            onClear={event => onClear(event, setStatusSelected, 'status')}
                            disabled={filterIsLoading}
                          />
                        </Grid>

                        <Grid item xs={3}>
                          <FormAutoComplete
                            id='situation'
                            label='Situação'
                            value={situationSelected}
                            onChange={(_, newValue) =>
                              onAutoCompleteSelectedFilter(newValue as Option, 'authority_status', setSituationSelected)
                            }
                            options={situationList}
                            onClear={event => onClear(event, setSituationSelected, 'authority_status')}
                            disabled={filterIsLoading}
                          />
                        </Grid>

                        <Grid item xs={3}>
                          <FormAutoComplete
                            id='authority'
                            label='Autoridade'
                            value={authoritySelected}
                            onChange={(_, newValue) =>
                              onAutoCompleteSelectedFilter(newValue as Option, 'authority_name', setAuthoritySelected)
                            }
                            options={auhtorityList}
                            onClear={event => onClear(event, setAuthoritySelected, 'authority_name')}
                            disabled={filterIsLoading}
                          />
                        </Grid>

                        <Grid item xs={3}>
                          <FormAutoComplete
                            id='paymentStatus'
                            label='Status do Boleto'
                            value={paymentStatusSelected}
                            onChange={(_, newValue) =>
                              onAutoCompleteSelectedFilter(
                                newValue as Option,
                                'invoice_status',
                                setPaymentStatusSelected
                              )
                            }
                            options={paymentStatusList}
                            onClear={event => onClear(event, setPaymentStatusSelected, 'invoice_status')}
                            disabled={filterIsLoading}
                          />
                        </Grid>

                        <Grid item xs={3}>
                          <FormAutoComplete
                            id='plateOwner'
                            label='Responsável pelo Veículo'
                            options={plateOwnerList}
                            value={plateOwner ? plateOwner.label : ''}
                            onChange={(_, newValue) => onChangeFilterPlateOwner(newValue as Option)}
                            onClear={() => setPlateOwner(null)}
                            disabled={filterIsLoading}
                          />
                        </Grid>

                        <Grid item xs={12}>
                          <Grid container spacing={2}>
                            <Grid item xs={3}>
                              <FormGroup>
                                <FormSwitch
                                  label='Encaminhado ao jurídico'
                                  checked={isLegalExternal}
                                  disabled={isLegalExternal || filterIsLoading}
                                  handleOnChange={(_event, checked) => {
                                    onToggle('forwardedToLegal', checked);
                                  }}
                                />
                              </FormGroup>
                            </Grid>

                            <Grid item xs={3}>
                              <FormGroup>
                                <FormSwitch
                                  label='Passível de Defesa/Recurso'
                                  disabled={isLegalExternal || filterIsLoading}
                                  handleOnChange={(_event, checked) => {
                                    onToggle('defensible', checked);
                                  }}
                                />
                              </FormGroup>
                            </Grid>

                            <Grid item xs={3}>
                              <FormGroup>
                                <FormSwitch
                                  label='Pendente de envio ao financeiro'
                                  disabled={isLegalExternal || filterIsLoading}
                                  handleOnChange={(_event, checked) => {
                                    onToggle('pendingForwardFinancial', checked);
                                  }}
                                />
                              </FormGroup>
                            </Grid>

                            <Grid item xs={3}>
                              <FormGroup>
                                <FormSwitch
                                  label='Encaminhado ao financeiro'
                                  disabled={isLegalExternal || filterIsLoading}
                                  handleOnChange={(_event, checked) => {
                                    onToggle('forwardedToFinancial', checked);
                                  }}
                                />
                              </FormGroup>
                            </Grid>
                          </Grid>
                        </Grid>

                        <Grid item xs={3}>
                          <FormSelect
                            id='filterBy'
                            label='Filtrar por'
                            value={filterBy}
                            onChange={event => onChangeFilterBy(event.target.value)}
                            options={filterByOptions}
                            onClear={() => setFilterBy('')}
                            disabled={filterIsLoading}
                          />
                        </Grid>

                        <Grid item xs={3}>
                          <FormSelect
                            id='filterByPeriod'
                            label='Período'
                            value={filterByPeriod}
                            onChange={event => onChangeFilterByPeriod(event.target.value)}
                            options={filterByPeriodOptions}
                            onClear={() => setFilterByPeriod('')}
                            disabled={filterIsLoading}
                          />
                        </Grid>

                        {filterByPeriod === 'custom' && (
                          <>
                            <Grid item xs={3}>
                              <FormText
                                type='date'
                                InputLabelProps={{ shrink: true }}
                                label='Data Inicial'
                                value={initialDate}
                                onChange={e => setinitialDate(e.target.value)}
                                disabled={filterIsLoading}
                              />
                            </Grid>

                            <Grid item xs={3}>
                              <FormText
                                type='date'
                                InputLabelProps={{ shrink: true }}
                                label='Data Final'
                                value={finalDate}
                                onChange={e => setfinalDate(e.target.value)}
                                disabled={filterIsLoading}
                              />
                            </Grid>
                          </>
                        )}

                        <Grid
                          container
                          item
                          xs={filterByPeriod === 'custom' ? 12 : 3}
                          justifyContent={`flex-${filterByPeriod === 'custom' ? 'end' : 'start'}`}
                          alignItems={`flex-${filterByPeriod === 'custom' ? 'end' : 'start'}`}
                        >
                          <CustomLoadingButton
                            variant='contained'
                            size='medium'
                            color='inherit'
                            onClick={() => {
                              clearAllFilters();
                              fetchData();
                            }}
                            disabled={!hasFilterSelected || filterIsLoading}
                            disableElevation
                          >
                            Limpar
                          </CustomLoadingButton>
                          &nbsp;
                          <CustomLoadingButton
                            variant='contained'
                            size='medium'
                            onClick={onFilter}
                            loading={filterIsLoading}
                            disabled={!hasFilterSelected}
                            className='submit-button'
                            disableElevation
                          >
                            Aplicar
                          </CustomLoadingButton>
                        </Grid>
                      </Grid>
                    </CustomAccordionDetails>
                  </CustomAccordion>
                )}
              </FiltersContainer>

              {isInitialLoading ? (
                <Loader />
              ) : (
                <Box
                  sx={{
                    position: 'relative',
                    ...(isLoading && {
                      '& td:first-of-type .MuiCheckbox-root, & th:first-of-type .MuiCheckbox-root': {
                        opacity: 0.3,
                        pointerEvents: 'none'
                      },
                      '& td:first-of-type .MuiCheckbox-root:hover, & th:first-of-type .MuiCheckbox-root:hover': {
                        backgroundColor: 'transparent'
                      },
                      '& td:first-of-type .MuiIconButton-root:hover, & th:first-of-type .MuiIconButton-root:hover': {
                        backgroundColor: 'transparent'
                      },
                      '& tbody tr:hover': {
                        backgroundColor: 'transparent !important'
                      }
                    })
                  }}
                >
                  {isLoading && (
                    <Box
                      sx={{
                        position: 'absolute',
                        inset: 0,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        backgroundColor: 'rgba(255, 255, 255, 0.6)',
                        zIndex: 1
                      }}
                    >
                      <CircularProgress size={40} />
                    </Box>
                  )}
                  <DataTable
                    columns={columns}
                    data={formattedData}
                    optionsObj={optionsObj}
                    customToolbar={
                      formattedData?.length !== 0 ? (
                        <div className='custom-toolbar-action'>
                          <CustomLoadingButton
                            startIcon={<FileDownloadIcon />}
                            variant='outlined'
                            onClick={onExport}
                            loading={exportIsLoading}
                            disabled={exportIsLoading}
                          >
                            Exportar
                          </CustomLoadingButton>
                        </div>
                      ) : undefined
                    }
                  />
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </>
  );
}

export default TrafficTickets;
